import {
  animate,
  state,
  style,
  transition,
  trigger
} from '@angular/animations';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Select, Store } from '@ngxs/store';
import { Deal } from 'app/models/dto/deal.model';
import { LocaleSettingService } from 'app/services/i18n/locale-setting.service';
import { LenderUserState } from 'ng2-app/store/lender-user/lender-user/lender-user.state';
import { map, Observable, Subject, takeUntil, combineLatest } from 'rxjs';
import { OfferOptionsState } from 'ng2-app/store/offers/offer-options.state';
import { OfferState } from 'ng2-app/store/offers/offer.state';
import { ApprovalsState } from 'ng2-app/store/offers/approvals.state';
import { BorrowerState } from 'ng2-app/store/borrower/borrower/borrower.state';
import { Borrower } from 'ng2-app/models/dto/borrower.model';
import { DealUtility } from 'ng2-app/util/deal/deal.utility';

@Component({
  selector: 'app-deals-table',
  templateUrl: './deals-table.component.html',
  styleUrls: ['./deals-table.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed, void', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
      transition('expanded <=> void', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'))
    ])
  ]
})
export class DealsTableComponent implements OnInit, AfterViewInit, OnDestroy {

  defaultActiveColumns = [
    'loopRank',
    'lenderProduct',
    'offerAmount',
    'offerPayback',
    'offerRate',
    'offerTerm',
    'offerFrequency',
    'offerPayment',
    'offerPoints',
    'status'
  ];

  @Input() deals$: Observable<Deal[]>;
  @Input() borrowerId: number;
  @Input() history = false;
  @Input() loading = false;
  @Input() bulkSubmitting = false;
  @Input() readOnly = false;
  @Input() showDealRightColumn: boolean = true;
  @Input() showDealsPageLink: boolean = false;
  @Input() activeColumns = this.defaultActiveColumns;
  @Input() defaultSortColumn: string = 'loopRank';
  @Output() submit = new EventEmitter<boolean>();
  @Select(LenderUserState.hasPermission('canSeeDealStatusRestrictions')) canSeeDealStatusRestrictions$: Observable<boolean>;
  @Select(BorrowerState.borrower) borrower$: Observable<Borrower>;

  destroyed$ = new Subject<boolean>();
  tableDataSource$: Observable<MatTableDataSource<Deal>>;
  columnsToDisplay: string[];
  currencySymbol: string | null;
  historyColumns = [
    'expand',
    'lenderProduct',
    'lastApplicationDate',
    'status',
    'reason',
    'comment',
    'renewal'
  ];
  expandedDeals: Deal[] = [];

  stageSortingWeights = {
    application: 1,
    underwriting: 2,
    funded: 3,
    funding: 3,
  };

  @ViewChild(MatSort) sort: MatSort;

  constructor(
    private store: Store,
    private localeSettingService: LocaleSettingService,
  ) { }

  ngOnInit(): void {
    this.columnsToDisplay = !this.history ? this.activeColumns : this.historyColumns;
    this.currencySymbol = this.localeSettingService.getLocaleCurrencySymbol();
    this.initExpandedDeals();
  }

  initExpandedDeals() {
    this.deals$.pipe(
      takeUntil(this.destroyed$),
      map( deals => {
        const savedExpandedDeals = this.savedExpandedDeals()[`${this.borrowerId}`];
        savedExpandedDeals.forEach( savedDealId => {
          const deal = deals.find( d => d.id === savedDealId);
          if (deal) {
            this.expandedDeals.push(deal);
            if(this.readOnly) {
              const statusOption = deal.statusOptions.filter( opt => opt.value === deal.status)[0];
              deal.statusDisplay = statusOption?.display || '';
            }
          }
        });
      })
    );
  }

  ngAfterViewInit() {
    this.tableDataSource$ = this.deals$.pipe(
      takeUntil(this.destroyed$),
      map((deals) => {
        const tableDataSource = new MatTableDataSource<Deal>();
        tableDataSource.sortingDataAccessor = this.sortingPropAccessor.bind(this);
        tableDataSource.sort = this.sort;
        setTimeout(() => {
          if (this.sort && this.sort.sortables.size > 0) {
            tableDataSource.sort = this.sort;
            if (this.history) {
              this.sort.direction = 'desc';
              this.sort.active = 'lastApplicationDate';
              tableDataSource.sortData = this.sortDealsByStage.bind(this);
            }

            this.sort.sortChange.emit();
          }
        });
        tableDataSource.data = [...deals];
        return tableDataSource;
      }),
    );
  }

  sortingPropAccessor(deal, property) {
    switch (property) {
      case 'lenderProduct':
      case 'lender':
        return deal.loan_product_lender.name;
      case 'loanProduct':
        return deal.loan_product.category.shortName;
      case 'offerAmount':
        return Number(deal.termsValues?.amount || 0);
      case 'offerPayback':
        return Number(deal.termsValues?.payback || 0);
      case 'offerRate':
        return Number(deal.termsValues?.rate || 0);
      case 'offerTerm':
        return Number(deal.termsValues?.term || 0);
      case 'offerFrequency':
        return Number(deal.termsValues?.frequency || 0);
      case 'offerPayment':
        return Number(deal.termsValues?.payment || 0);
      case 'offerFeesPerDollar':
        return Number(deal.termsValues?.fees || 0);
      case 'offerPoints':
        return Number(deal.termsValues?.points || 0);
      case 'loopRank':
        return Number(deal.loopRank || 999);
      default: return deal[property];
    }
  }

  sortDealsByStage(deals, sort: MatSort): Deal[] {
    return deals.sort((a, b) => {
      const dateA = this.getLastAppDateNumber(a),
        dateB = this.getLastAppDateNumber(b);
      const weightA = this.getSortingWeight(a),
        weightB = this.getSortingWeight(b);
      const byDate = sort.direction === 'asc' ? dateA - dateB : dateB - dateA;
      const byWeight = sort.direction === 'asc' ? weightA - weightB : weightB - weightA;
      return a.stage === b.stage ? byDate : byWeight;
    });
  }

  getSortingWeight(deal): number {
    return this.stageSortingWeights[deal.stage] || 0;
  }

  getLastAppDateNumber(deal: Deal) {
    return deal.lastApplicationDate ? new Date(deal.lastApplicationDate).getTime() : 0;
  }

  getCountPublishedOffers(deal: Deal) {
    const dealOffers$ =  this.store.select(OfferState.dealOffers).pipe(map(filterFn => filterFn(deal.id, deal.stage)));
    const dealOptions$ = this.store.select(OfferOptionsState.dealOptions).pipe(map(filterFn => filterFn(deal.id)));
    const dealApprovals$ = this.store.select(ApprovalsState.forDeal).pipe(map(filterFn => filterFn(deal.id)));

    return combineLatest([dealOffers$, dealOptions$, dealApprovals$, this.borrower$]).pipe(
      takeUntil(this.destroyed$),
      map(([offers, offerOptions, approvals, borrower]) => {
        const publishedOffers = (offers ?? []).filter((offer) =>  Boolean(offer.publishedToBorrower));
        const publishedOptions = (offerOptions ?? []).filter((offerOption) => Boolean(offerOption.publishedToBorrower));
        const publishedApprovals = (approvals ?? []).filter((approval) => (
          Boolean(approval.publishedToBorrower)
          && Boolean(approval.publishedByLenderUserId)
        ));

        if (borrower?.isEmbedded || borrower?.isIntelligentLending || borrower?.user_experiments?.some((e) => e.experimentId === 34)) {
          return publishedOffers.length + publishedOptions.length + publishedApprovals.length;
        }
        return publishedOffers.length + publishedOptions.length;
      })
    );
  }

  getDealInactiveReason(deal: Deal) {
    return deal.last_inactive_status && deal.last_inactive_status.reason ? deal.last_inactive_status.reason.split(', ')[0] : '—';
  }

  getAdditionalInactiveReasons(deal: Deal) {
    const additionalReasons = deal.last_inactive_status && deal.last_inactive_status.reason
      ? deal.last_inactive_status.reason.split(', ')
      : [];

    if (deal.dead_disposition_deals) {
      deal.dead_disposition_deals.forEach( (deadDeal) => {
        if (deadDeal.lenderReason) {
          additionalReasons.push(deadDeal.lenderReason);
        }
      });
    }

    if (additionalReasons.length > 1) {
      additionalReasons.shift();
      return additionalReasons.join(', ');
    }

    return 'No additional reasons provided';
  }

  getDealCommentTooltip(deal: Deal) {
    return deal.last_inactive_status && deal.last_inactive_status.otherReason ? deal.last_inactive_status.otherReason : 'No lender notes';
  }

  dealTrackBy(index: number, deal: Deal) {
    return deal.id;
  }

  toggleExpanded(deal: Deal) {
    const savedExpandedDeals = this.savedExpandedDeals();
    if (this.dealExpanded(deal)) {
      this.expandedDeals = this.expandedDeals.filter(d => d.id !== deal.id);
      savedExpandedDeals[this.borrowerId] = savedExpandedDeals[this.borrowerId].filter(id => id !== deal.id);
    } else {
      this.expandedDeals.push(deal);
      if (savedExpandedDeals[this.borrowerId]) {
        savedExpandedDeals[this.borrowerId] = [ ...savedExpandedDeals[this.borrowerId], deal.id ];
      } else {
        savedExpandedDeals[this.borrowerId] = [deal.id];
      }
    }
    localStorage.setItem('expandedDeals', JSON.stringify(savedExpandedDeals));
  }

  dealExpanded(deal: Deal) {
    return this.expandedDeals.filter(d => d.id === deal.id).length > 0 ? true : false;
  }

  getLoanType(deal: Deal) {
    return DealUtility.getOverriddenLoanType(deal) ?? deal.loan_product.category.shortName;
  }

  ngOnDestroy() {
    this.destroyed$.complete();
  }

  private savedExpandedDeals(): { [key: number]: number[] } {
    return JSON.parse(localStorage.getItem('expandedDeals') || '{}');
  }

}
